import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { toApiError } from '@/api/errorHandler';
import { getStoredToken } from '@/api/interceptors';
import type { ApiError, User } from '@/models';
import { authService, type LoginCredentials } from './authService';
import type { AsyncStatus } from '@/types';

export interface AuthState {
  user: User | null;
  token: string | null;
  status: AsyncStatus;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: getStoredToken(),
  status: 'idle',
  error: null,
};

export const login = createAsyncThunk<{ user: User; token: string }, LoginCredentials, { rejectValue: ApiError }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const session = await authService.login(credentials);
      return { user: session.user, token: session.token };
    } catch (err) {
      return rejectWithValue(toApiError(err));
    }
  },
);

export const restoreSession = createAsyncThunk<User, void, { rejectValue: ApiError }>(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      return await authService.me();
    } catch (err) {
      return rejectWithValue(toApiError(err));
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    sessionExpired(state) {
      state.user = null;
      state.token = null;
      state.status = 'idle';
    },
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message ?? 'Login failed';
      })
      .addCase(restoreSession.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = 'succeeded';
      })
      .addCase(restoreSession.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.status = 'idle';
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.status = 'idle';
        state.error = null;
      });
  },
});

export const { sessionExpired, setUser } = authSlice.actions;
export default authSlice.reducer;
